import { useEffect, useState } from 'react';

import { RootState } from 'store/index';

export type LiveAssistMode = 'live' | 'ended' | 'completed';

export interface LiveAssistTarget {
  callId?: number;
  mode?: LiveAssistMode;
  attempt: number;
}

export interface LiveAssistCallMemory {
  current?: number;
  lastLive?: number;
  ended: number[];
  attempt: number;
}

export const FRESH_CALL_MEMORY: LiveAssistCallMemory = { ended: [], attempt: 0 };

export const LiveCallScribeCallSelector = (state: RootState): number | undefined =>
  state.callScribe.protectedTranscriptCallId;

const isId = (value: number | undefined): value is number => typeof value === 'number';

const endCurrent = (memory: LiveAssistCallMemory): number[] =>
  isId(memory.current) && !memory.ended.includes(memory.current)
    ? [...memory.ended, memory.current]
    : memory.ended;

export const rememberCall = (
  memory: LiveAssistCallMemory,
  liveCallId: number | undefined,
  allowRedial: boolean,
): LiveAssistCallMemory => {
  const live =
    isId(liveCallId) && (allowRedial || !memory.ended.includes(liveCallId))
      ? liveCallId
      : undefined;
  if (live === memory.current) return memory;
  if (!isId(live)) return { ...memory, current: undefined, ended: endCurrent(memory) };
  return {
    current: live,
    lastLive: live,
    ended: endCurrent(memory).filter(id => id !== live),
    attempt: memory.attempt + 1,
  };
};

export const liveAssistTarget = (
  browsedCallId: number | undefined,
  memory: LiveAssistCallMemory,
): LiveAssistTarget => {
  const { current, lastLive, attempt } = memory;
  if (isId(current)) return { callId: current, mode: 'live', attempt };
  if (isId(lastLive) && (!isId(browsedCallId) || browsedCallId === lastLive)) {
    return { callId: lastLive, mode: 'ended', attempt };
  }
  if (isId(browsedCallId)) return { callId: browsedCallId, mode: 'completed', attempt };
  return { attempt };
};

export const useLiveAssistTarget = (
  liveCallId: number | undefined,
  browsedCallId: number | undefined,
  allowRedial = false,
): LiveAssistTarget => {
  const [memory, setMemory] = useState<LiveAssistCallMemory>(FRESH_CALL_MEMORY);
  useEffect(() => {
    setMemory(previous => rememberCall(previous, liveCallId, allowRedial));
  }, [liveCallId, allowRedial]);
  return liveAssistTarget(browsedCallId, rememberCall(memory, liveCallId, allowRedial));
};
